// src/features/system/admin/api/schedulerApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Scheduler — FE API layer.
//
// Types + fetch functions for the calendar scheduler.
// All calls go through /bff/scheduler/* endpoints.
// Tenant-scoped: the BFF auto-isolates data per tenant via session context.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/scheduler';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SchedulerEvent {
  id: string;
  id_scope?: number | null;
  title: string;
  date: string;             // YYYY-MM-DD
  start_time: string;       // HH:MM
  end_time: string;         // HH:MM
  start_slot: number;       // 0-95 (15-min index)
  end_slot: number;         // exclusive
  color: string;
  notes: string;
  all_day: boolean;
  recurring: 'none' | 'daily' | 'weekly' | 'monthly';
  active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  recurring_source?: string;  // Set on virtual recurring instances
  conflicts?: { id: string; title: string; start_time: string; end_time: string }[];
}

export interface SchedulerEventInput {
  id?: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  start_slot: number;
  end_slot: number;
  color?: string;
  notes?: string;
  all_day?: boolean;
  recurring?: string;
  source?: string;
}

export interface LockState {
  locked: boolean;
  locked_by: string | null;
  locked_at: string | null;
  reason: string;
}

export interface EventListResponse {
  items: SchedulerEvent[];
  next_cursor: string | null;
}

// ── Marker types ────────────────────────────────────────────────────────────

export interface Marker {
  id: string;
  id_scope?: number | null;
  label: string;
  timestamp: string;          // ISO8601
  date: string;               // YYYY-MM-DD (derived from timestamp)
  marker_type: 'deployment' | 'incident' | 'shift' | 'milestone' | 'custom';
  color: string;
  icon: string | null;
  notes: string;
  source: string;
  active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface MarkerInput {
  id?: string;
  label: string;
  timestamp: string;
  marker_type?: string;
  color?: string;
  icon?: string;
  notes?: string;
  source?: string;
}

export interface MarkerListResponse {
  items: Marker[];
  next_cursor: string | null;
}

// ── React Query cache keys ────────────────────────────────────────────────

export const SCHEDULER_KEYS = {
  all: ['scheduler'] as const,
  events: () => [...SCHEDULER_KEYS.all, 'events'] as const,
  eventsByRange: (from: string, to: string) =>
    [...SCHEDULER_KEYS.all, 'events', from, to] as const,
  event: (id: string) => [...SCHEDULER_KEYS.all, 'event', id] as const,
  markers: () => [...SCHEDULER_KEYS.all, 'markers'] as const,
  markersByRange: (from: string, to: string) =>
    [...SCHEDULER_KEYS.all, 'markers', from, to] as const,
  marker: (id: string) => [...SCHEDULER_KEYS.all, 'marker', id] as const,
  timeline: (from: string, to: string) =>
    [...SCHEDULER_KEYS.all, 'timeline', from, to] as const,
  lock: () => [...SCHEDULER_KEYS.all, 'lock'] as const,
};

// ── Event endpoints ──────────────────────────────────────────────────────

export async function fetchEvents(
  from?: string,
  to?: string,
): Promise<EventListResponse> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const q = qs.toString();
  return bff.get<EventListResponse>(
    `${BASE}/events${q ? `?${q}` : ''}`,
  );
}

export async function fetchEvent(id: string): Promise<SchedulerEvent> {
  return bff.get<SchedulerEvent>(`${BASE}/events/${id}`);
}

export async function upsertEvent(
  input: SchedulerEventInput,
): Promise<SchedulerEvent> {
  return bff.post<SchedulerEvent, SchedulerEventInput>(
    `${BASE}/events`,
    input,
  );
}

export async function deleteEvent(id: string): Promise<{ deleted: boolean }> {
  return bff.del<{ deleted: boolean }>(`${BASE}/events/${id}`);
}

// ── Lock endpoints ───────────────────────────────────────────────────────

export async function fetchLockState(): Promise<LockState> {
  return bff.get<LockState>(`${BASE}/lock`);
}

export async function setLockState(
  locked: boolean,
  reason?: string,
): Promise<LockState> {
  return bff.put<LockState, { locked: boolean; reason?: string }>(
    `${BASE}/lock`,
    { locked, reason },
  );
}

// ── Marker endpoints ────────────────────────────────────────────────────

export async function fetchMarkers(
  from?: string,
  to?: string,
): Promise<MarkerListResponse> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const q = qs.toString();
  return bff.get<MarkerListResponse>(
    `${BASE}/markers${q ? `?${q}` : ''}`,
  );
}

export async function fetchMarker(id: string): Promise<Marker> {
  return bff.get<Marker>(`${BASE}/markers/${id}`);
}

export async function upsertMarker(
  input: MarkerInput,
): Promise<Marker> {
  return bff.post<Marker, MarkerInput>(
    `${BASE}/markers`,
    input,
  );
}

export async function deleteMarker(id: string): Promise<{ deleted: boolean }> {
  return bff.del<{ deleted: boolean }>(`${BASE}/markers/${id}`);
}

// ── Timeline endpoint ───────────────────────────────────────────────────

export interface TimelineEntry {
  type: 'event' | 'marker';
  id: string;
  label: string;
  timestamp: string;
  date: string;
  color: string;
  source: string;
  created_by: string | null;
  detail: string;
}

export interface TimelineResponse {
  items: TimelineEntry[];
  total: number;
}

export async function fetchTimeline(
  from?: string,
  to?: string,
  limit?: number,
): Promise<TimelineResponse> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (limit) qs.set('limit', String(limit));
  const q = qs.toString();
  return bff.get<TimelineResponse>(
    `${BASE}/timeline${q ? `?${q}` : ''}`,
  );
}
